import React, { memo, useRef, useMemo, useState, useReducer, useCallback /* useEffect */ } from 'react';
import { Card } from 'tea-component';
import { useRequest, useDebounceFn } from 'ahooks';
import { cloneDeep } from 'lodash-es';
import Loading from '@gp/components/Loading';
import InfoBubble from '@gp/components/InfoBubble';
// import PencilEdit from '@gp/components/PencilEdit';
import ArchiveEditForm from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/components/StakeholderEdit/components/ArchiveEditForm';
import stakeholdersDataReducer from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/components/StakeholderEdit/reducers/stakeholdersDataReducer';
import { editStakeholders } from '@gp/services/stakeholder';
import { DEPARTMENT_NAME_MAPS } from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/consts';
import {
  getInitialStakeholdersData,
  createReqParams,
} from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/components/StakeholderEdit/utils';
import { reportOperationLog } from '@gp/services/global';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import GroupSvg from '@gp/assets/svg/group.svg';
import '@gp/pages/Pipeline/ProjectDetails/Stakeholder/components/StakeholderEdit/index.scss';

interface StakeholderEditProps {
  projectDetailData: any;
  initialData: any;
  onCancel: () => void;
}

const StakeholderEdit: React.FC<any> = (props: StakeholderEditProps) => {
  const { initialData, projectDetailData, onCancel } = props;

  const { loading, runAsync: editStakeholdersRun } = useRequest(editStakeholders, { manual: true });

  const initialStakeholdersData = getInitialStakeholdersData(initialData);

  const cachedStakeholdersData = useRef(cloneDeep(initialStakeholdersData));

  const [stakeholdersData, stakeholdersDataDispatch] = useReducer(stakeholdersDataReducer, {
    past: [],
    present: initialStakeholdersData,
    future: [],
  });

  const [showError, setShowError] = useState(false);

  const validation = useMemo<{ isValid: boolean; msg: string }>(() => {
    const result = {
      isValid: true,
      msg: '',
    };
    try {
      // 第一层遍历 Project Core Stakeholders模块、Studio-Level Stakeholders模块
      for (let i = 0; i < stakeholdersData?.present?.length; i++) {
        const stakeholderData = stakeholdersData?.present?.[i];
        // 第二层遍历 IEGG、Studio
        for (let j = 0; j < stakeholderData?.organization_list?.length; j++) {
          const organization = stakeholderData?.organization_list?.[j] || {};
          const ownerList = organization?.owner_list || [];
          // 如果只有一个project owner选矿，并且没有选择project owner，就不校验
          if (ownerList?.length === 1 && !ownerList?.[0]?.project_owner) continue;
          // 第三层遍历 Development、Publishing、Finance
          for (let k = 0; k < ownerList?.length; k++) {
            const owner = ownerList[k] || {};
            if (!owner?.project_owner) {
              result.isValid = false;
              return result;
            }
            const emps = owner?.list || [];
            // 第四层遍历 每一个人员
            for (let kk = 0; kk < emps?.length; kk++) {
              const emp = emps[kk] || {};
              if (!emp.name || !emp.position || emp?.linkStatus === 'error') {
                result.isValid = false;
                return result;
              }
            }
          }
        }
      }
    } catch (exception: any) {
      console.warn(exception);
      result.isValid = false;
      result.msg = exception;
    }
    return result;
  }, [stakeholdersData?.present]);

  const handleConfirm = useCallback(async () => {
    setShowError(true);
    if (!validation?.isValid) return;
    const params = createReqParams(cachedStakeholdersData.current, stakeholdersData?.present);
    await editStakeholdersRun({
      ...params,
      project_id: projectDetailData?.project_id || '',
    });
    reportOperationLog(ALL_REPORT_OPERATION_LOG.DETAIL_STAKEHOLDER_IEGG_EDIT_SUBMIT, 'button');
    if (onCancel instanceof Function) {
      onCancel();
    }
  }, [projectDetailData, stakeholdersData?.present, editStakeholdersRun, onCancel]);
  const handleCancel = useCallback(() => {
    setShowError(false);
    if (onCancel instanceof Function) {
      onCancel();
    }
  }, [onCancel]);

  const { run: handleConfirmDebounced } = useDebounceFn(handleConfirm, { wait: 300 });

  return (
    <div className="gp-detail-stakeholder-details-edit">
      {loading && <Loading style={{ position: 'unset' }} loading loadingBg="#f2f3f5" />}
      {!loading &&
        stakeholdersData?.present?.map((stakeholder: any) => (
          <Card key={stakeholder?.stakeholder} className="stakeholder-item">
            <Card.Body
              title={
                <>
                  <img className="titlebar-icon" src={GroupSvg} alt="" />
                  <span className="titlebar-text">{stakeholder?.stakeholder}</span>
                </>
              }
              operation={
                stakeholder?.stakeholder === 'Project Core Stakeholders' && (
                  <>
                    <InfoBubble
                      className="globalpedia-modal-font14"
                      placement="top-end"
                      customBubbleContent={
                        validation?.isValid ? '' : 'Please check that the information is complete or correct.'
                      }
                      DomOrText={
                        <>
                          <a className="gp-detail-stakeholder-details-operation-btn" onClick={handleConfirmDebounced}>
                            Confirm
                          </a>
                        </>
                      }
                    />
                    <a className="gp-detail-stakeholder-details-operation-btn" onClick={handleCancel}>
                      Cancel
                    </a>
                  </>
                )
              }
            >
              <ul className="org-list">
                {stakeholder?.organization_list?.map((organization: any) => (
                  <li key={organization?.organization} className="org-item">
                    <h2 className="org-name">
                      <span></span>
                      <span>{DEPARTMENT_NAME_MAPS[organization?.organization]}</span>
                    </h2>
                    <ArchiveEditForm
                      showError={showError}
                      validation={validation}
                      organization={organization?.organization}
                      ownerList={organization?.owner_list}
                      stakeholdersDataDispatch={stakeholdersDataDispatch}
                      projectDetailData={projectDetailData}
                    />
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        ))}
    </div>
  );
};

export default memo(StakeholderEdit);
